import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import * as simpleAuth from '../utils/simpleAuth';

/**
 * Custom hook for authentication management backed by Supabase auth.
 * Establishing a real Supabase session is what makes RLS-protected reads work.
 */
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loginData, setLoginData] = useState({ username: '', password: '', isSignup: false });
  const [isLoading, setIsLoading] = useState(true);

  // Restore any persisted Supabase session on mount
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const currentUser = await simpleAuth.initAuth();
      if (cancelled) return;

      if (currentUser) {
        setUser(currentUser);
        setUserRole(currentUser.role);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setUserRole(null);
      }

      setIsLoading(false);
    })();

    return () => { cancelled = true; };
  }, []);

  // Handle login
  const handleLogin = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('🔐 Attempting login for:', loginData.username);

      const result = await simpleAuth.login(loginData.username, loginData.password);

      if (result.success) {
        console.log('✅ Login successful:', result.user);
        setUser(result.user);
        setUserRole(result.user.role);
        setIsAuthenticated(true);
        toast.success(`Welcome back, ${result.user.username}!`);
        return true;
      } else {
        console.error('❌ Login failed:', result.error);
        toast.error(result.error || 'Invalid email or password');
        return false;
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      toast.error('Login failed. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [loginData]);

  // Handle Google sign-in (redirects away; the session lands on the return trip)
  const handleGoogleLogin = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await simpleAuth.loginWithGoogle();

      if (!result.success) {
        toast.error(result.error || 'Google sign-in failed');
        setIsLoading(false);
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ Google login error:', error);
      toast.error('Google sign-in failed. Please try again.');
      setIsLoading(false);
      return false;
    }
  }, []);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      await simpleAuth.logout();
      setIsAuthenticated(false);
      setUser(null);
      setUserRole(null);
      setLoginData({ username: '', password: '', isSignup: false });
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if something fails
      setIsAuthenticated(false);
      setUser(null);
      setUserRole(null);
      setLoginData({ username: '', password: '', isSignup: false });
      toast.error('Logout failed, but local session cleared');
    }
  }, []);

  // Handle signup (not implemented for simple auth)
  const handleSignup = useCallback(async (email, password, username) => {
    toast.error('Signup is not available. Please contact an administrator.');
    return false;
  }, []);

  return {
    isAuthenticated,
    user,
    userRole,
    loginData,
    setLoginData,
    handleLogin,
    handleGoogleLogin,
    handleLogout,
    handleSignup,
    isLoading
  };
};
